import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import API_ENDPOINTS from "../../utils/apiEndpoints";

const ManualAdmission = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // Dropdown States
    const [classes, setClasses] = useState([]);
    const [casteCategory, setCasteCategory] = useState([]);
    const [mediums, setMediums] = useState([]);
    const [genders, setGenders] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [countries, setCountries] = useState([]);
    const [blood_groups, setBloodGroups] = useState([]);
    const [boards, setBoards] = useState([]);
    const [co_curricular_activities, setCoCurricularActivities] = useState([]);
    const [extraCurricular, setExtraCurricular] = useState([]);

    const [hasHealthIssues, setHasHealthIssues] = useState('no');

    const [formData, setFormData] = useState({
        // Basic Info
        first_name: '', last_name: '', gender_id: '', dob: '', birth_place: '',
        nationality: '', religion: '', caste_category_id: '', caste: '',
        mother_tongue: '', student_email: '', blood_group_id: '',

        // Address Info
        address1: '', address2: '', city: '', taluk: '', district: '', state: '',
        zip_code: '', country_id: '', mobile: '', whatsapp_no: '',

        // Parent Info
        father_name: '', father_occupation: '', mother_name: '', mother_occupation: '',
        father_adhaar_no: '', mother_adhaar_no: '', email: '', parent_annual_income: '',

        // Academic Info
        class_id: '', medium_id: '', prev_school_name: '', prev_school_address: '',
        prev_school_pincode: '', board_id: '', candidate_sats_no: '',
        candidate_pen_no: '', candidate_adhaar_no: '', siblings_in_ssv: 'no',
        siblings_data: '', been_to_sbv: 'no', co_curricular_activity_id: '',
        extra_curricular_activity_id: [],
        candidate_aim: '', achievements: '',

        // Nested Marks Array (Passed to Backend as JSON encoded text)
        last_annual_exam_marks: {
            marksofClass: '',
            English: '',
            Kannada: '',
            language_id: '',
            LanguageMarks: '',
            Maths: '',
            Science: '',
            Social: '',
            Total: '',
            PercentageCGPA: '',
        },

        status: 'Approved'
    });

    const [fileData, setFileData] = useState({
        health_file: null, student_photo: null, student_adhaar: null,
        student_signature: null, father_signature: null, mother_signature: null,
        mother_adhaar: null, father_adhaar: null
    });

    useEffect(() => {
        const token = localStorage.getItem("adminToken");
        if (!token) {
            console.error("No token found in localStorage");
            return;
        }

        fetch(API_ENDPOINTS.DROPDOWN_DATA, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        })
            .then(response => response.json())
            .then(data => {
                if (data.data) {
                    setClasses(data.data.classes || []);
                    setCasteCategory(data.data.caste_categories || []);
                    setMediums(data.data.mediums || []);
                    setGenders(data.data.genders || []);
                    setLanguages(data.data.languages || []);
                    setCountries(data.data.countries || []);
                    setBloodGroups(data.data.blood_groups || []);
                    setBoards(data.data.boards || []);
                    setCoCurricularActivities(data.data.co_curricular_activities || []);
                    setExtraCurricular(data.data.extra_curricular_activities || []);
                }
            })
            .catch(error => console.error("Error fetching dropdowns:", error));
    }, []);

    const handleChange = (e) => {
        let name = e.target.name;
        let value = e.target.value.replace(/[^\x00-\x7F]/g, '');

        // Manually enforce max lengths to prevent autofill/faker tools from crashing the backend
        if (name === 'zip_code' || name === 'prev_school_pincode') value = value.substring(0, 6);
        if (name === 'mobile' || name === 'whatsapp_no') value = value.substring(0, 10);
        if (name === 'father_adhaar_no' || name === 'mother_adhaar_no' || name === 'candidate_adhaar_no') value = value.substring(0, 12);

        if (name === 'parent_annual_income') value = value.replace(/[^0-9.]/g, ''); // Numbers only

        setFormData({ ...formData, [name]: value });
    };

    const handleMarksChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            last_annual_exam_marks: {
                ...prev.last_annual_exam_marks,
                [name]: value.replace(/[^\x00-\x7F]/g, '')
            }
        }));
    };

    const handleExtraCurricularChange = (e) => {
        const { value, checked } = e.target;
        const activityId = Number(value);
        setFormData(prev => ({
            ...prev,
            extra_curricular_activity_id: checked
                ? [...prev.extra_curricular_activity_id, activityId]
                : prev.extra_curricular_activity_id.filter((id) => id !== activityId)
        }));
    };

    const handleFileChange = (e) => {
        setFileData({ ...fileData, [e.target.name]: e.target.files[0] });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem("adminToken");

            const payload = new FormData();

            // Textual fields (exclude dicts)
            Object.keys(formData).forEach(key => {
                if (key !== 'last_annual_exam_marks' && key !== 'extra_curricular_activity_id') {
                    payload.append(key, formData[key] || '');
                }
            });

            // Extra Curricular fields [arrays]
            formData.extra_curricular_activity_id.forEach(id => {
                payload.append('extra_curricular_activity_id[]', id);
            });

            // Flatten Nested Exam Marks to array format
            Object.keys(formData.last_annual_exam_marks).forEach(key => {
                payload.append(`last_annual_exam_marks[${key}]`, formData.last_annual_exam_marks[key]);
            });

            // Files
            Object.keys(fileData).forEach(key => {
                if (key === 'health_file') {
                    if (hasHealthIssues === 'yes' && fileData[key]) {
                        payload.append(key, fileData[key]);
                    }
                } else if (fileData[key]) {
                    payload.append(key, fileData[key]);
                }
            });

            const response = await axios.post(API_ENDPOINTS.ADMIN_MANUAL_ADMISSION, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data"
                }
            });
            alert('Manual Admission successfully saved!');
            navigate('/admin/dashboard');
        } catch (error) {
            console.error("Submission error", error);
            alert(error.response?.data?.message || 'Error occurred while saving admission.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto p-4 sm:p-8 bg-white shadow-xl rounded-lg mt-8 mb-8 text-sm">
            <h2 className="text-3xl font-bold text-center text-blue-700 mb-6 border-b pb-4">Manual Admission Entry</h2>

            <form onSubmit={handleSubmit} className="space-y-8">

                {/* 1. BASIC INFO */}
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                    <h3 className="text-xl font-bold text-blue-800 mb-4 border-b border-blue-300 pb-2">1. Basic Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div><label>First Name <span className="text-red-500">*</span></label><input required type="text" name="first_name" value={formData.first_name} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Last Name</label><input type="text" name="last_name" value={formData.last_name} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Gender <span className="text-red-500">*</span></label><select required name="gender_id" value={formData.gender_id} onChange={handleChange} className="w-full p-2 border rounded mt-1"><option value="">Select</option>{genders.map(g => <option key={g.id} value={g.id}>{g.gender}</option>)}</select></div>
                        <div><label>Date of Birth <span className="text-red-500">*</span></label><input required type="date" name="dob" value={formData.dob} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Place of Birth <span className="text-red-500">*</span></label><input required type="text" name="birth_place" value={formData.birth_place} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Nationality <span className="text-red-500">*</span></label><input required type="text" name="nationality" value={formData.nationality} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Religion <span className="text-red-500">*</span></label><input required type="text" name="religion" value={formData.religion} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Caste Category <span className="text-red-500">*</span></label><select required name="caste_category_id" value={formData.caste_category_id} onChange={handleChange} className="w-full p-2 border rounded mt-1"><option value="">Select</option>{casteCategory.map(c => <option key={c.id} value={c.id}>{c.caste_category}</option>)}</select></div>
                        <div><label>Caste <span className="text-red-500">*</span></label><input required type="text" name="caste" value={formData.caste} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Mother Tongue <span className="text-red-500">*</span></label><input required type="text" name="mother_tongue" value={formData.mother_tongue} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Student Email</label><input type="email" name="student_email" value={formData.student_email} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Blood Group <span className="text-red-500">*</span></label><select required name="blood_group_id" value={formData.blood_group_id} onChange={handleChange} className="w-full p-2 border rounded mt-1"><option value="">Select</option>{blood_groups.map(bg => <option key={bg.id} value={bg.id}>{bg.blood_group}</option>)}</select></div>
                    </div>
                </div>

                {/* 2. ADDRESS INFO */}
                <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                    <h3 className="text-xl font-bold text-purple-800 mb-4 border-b border-purple-300 pb-2">2. Address & Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="col-span-2"><label>Address Line 1 <span className="text-red-500">*</span></label><input required type="text" name="address1" value={formData.address1} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div className="col-span-2"><label>Address Line 2</label><input type="text" name="address2" value={formData.address2} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>City <span className="text-red-500">*</span></label><input required type="text" name="city" value={formData.city} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Taluk <span className="text-red-500">*</span></label><input required type="text" name="taluk" value={formData.taluk} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>District <span className="text-red-500">*</span></label><input required type="text" name="district" value={formData.district} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>State <span className="text-red-500">*</span></label><input required type="text" name="state" value={formData.state} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>PIN Code <span className="text-red-500">*</span></label><input required type="text" name="zip_code" value={formData.zip_code} onChange={handleChange} maxLength="6" className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Country <span className="text-red-500">*</span></label><select required name="country_id" value={formData.country_id} onChange={handleChange} className="w-full p-2 border rounded mt-1"><option value="">Select</option>{countries.map(c => <option key={c.id} value={c.id}>{c.country}</option>)}</select></div>
                        <div><label>Mobile Number <span className="text-red-500">*</span></label><input required type="text" name="mobile" value={formData.mobile} onChange={handleChange} maxLength="10" className="w-full p-2 border rounded mt-1 bg-yellow-50" /></div>
                        <div><label>WhatsApp Number</label><input type="text" name="whatsapp_no" value={formData.whatsapp_no} onChange={handleChange} maxLength="10" className="w-full p-2 border rounded mt-1" /></div>
                    </div>
                </div>

                {/* 3. PARENT INFO */}
                <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                    <h3 className="text-xl font-bold text-green-800 mb-4 border-b border-green-300 pb-2">3. Parent Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div><label>Father's Name</label><input type="text" name="father_name" value={formData.father_name} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Father's Occupation</label><input type="text" name="father_occupation" value={formData.father_occupation} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Father's Aadhaar No</label><input type="text" name="father_adhaar_no" value={formData.father_adhaar_no} onChange={handleChange} maxLength="12" className="w-full p-2 border rounded mt-1" /></div>

                        <div><label>Mother's Name</label><input type="text" name="mother_name" value={formData.mother_name} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Mother's Occupation</label><input type="text" name="mother_occupation" value={formData.mother_occupation} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Mother's Aadhaar No</label><input type="text" name="mother_adhaar_no" value={formData.mother_adhaar_no} onChange={handleChange} maxLength="12" className="w-full p-2 border rounded mt-1" /></div>

                        <div><label>Parent Email</label><input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Annual Income <span className="text-red-500">*</span></label><input required type="text" name="parent_annual_income" value={formData.parent_annual_income} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                    </div>
                </div>

                {/* 4. ACADEMIC INFO */}
                <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
                    <h3 className="text-xl font-bold text-yellow-800 mb-4 border-b border-yellow-300 pb-2">4. Academic Details</h3>

                    <div className="mb-6 grid md:grid-cols-2 gap-4">
                        <div>
                            <label>Class for which Admission is Sought <span className="text-red-500">*</span></label>
                            <select required name="class_id" value={formData.class_id} onChange={handleChange} className="w-full p-2 border rounded mt-1">
                                <option value="">Select Class</option>
                                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div><label>Candidate Aadhaar No <span className="text-red-500">*</span></label><input required type="text" name="candidate_adhaar_no" value={formData.candidate_adhaar_no} onChange={handleChange} maxLength="12" className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Candidate SATS No</label><input type="text" name="candidate_sats_no" value={formData.candidate_sats_no} placeholder="000 000 000" onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Candidate PEN No</label><input type="text" name="candidate_pen_no" value={formData.candidate_pen_no} placeholder="00000000000" onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>

                        <div><label>Previous School Name <span className="text-red-500">*</span></label><input required type="text" name="prev_school_name" value={formData.prev_school_name} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        <div className="col-span-2"><label>Previous School Address <span className="text-red-500">*</span></label><input required type="text" name="prev_school_address" value={formData.prev_school_address} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>

                        <div><label>Previous School PIN <span className="text-red-500">*</span></label><input required type="text" name="prev_school_pincode" value={formData.prev_school_pincode} onChange={handleChange} maxLength="6" className="w-full p-2 border rounded mt-1" /></div>
                        <div><label>Medium of Instruction <span className="text-red-500">*</span></label><select required name="medium_id" value={formData.medium_id} onChange={handleChange} className="w-full p-2 border rounded mt-1"><option value="">Select</option>{mediums.map(m => <option key={m.id} value={m.id}>{m.media}</option>)}</select></div>
                        <div><label>Board Affiliate <span className="text-red-500">*</span></label><select required name="board_id" value={formData.board_id} onChange={handleChange} className="w-full p-2 border rounded mt-1"><option value="">Select</option>{boards.map(b => <option key={b.id} value={b.id}>{b.board_name}</option>)}</select></div>
                    </div>

                    <div className="mt-8 border-t pt-4">
                        <label className="font-bold text-gray-700 block mb-2">Fill in the Results of the Candidate in the last Annual Examinations:</label>
                        <select
                            name="marksofClass"
                            required
                            value={formData.last_annual_exam_marks.marksofClass}
                            onChange={handleMarksChange}
                            className="w-1/3 p-2 border rounded mb-4"
                        >
                            <option value="">Select Class</option>
                            <option value="V">V</option>
                            <option value="VI">VI</option>
                            <option value="VII">VII</option>
                        </select>
                        <table className="w-full border-collapse border border-gray-300">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="border border-gray-300 p-2 text-left">Subject</th>
                                    <th className="border border-gray-300 p-2 text-left">Marks/Grades</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-gray-300 p-2">English</td>
                                    <td className="border border-gray-300 p-2"><input required name="English" value={formData.last_annual_exam_marks.English} onChange={handleMarksChange} className="w-full border rounded p-1" /></td>
                                </tr>
                                <tr>
                                    <td className="border border-gray-300 p-2">Kannada</td>
                                    <td className="border border-gray-300 p-2"><input required name="Kannada" value={formData.last_annual_exam_marks.Kannada} onChange={handleMarksChange} className="w-full border rounded p-1" /></td>
                                </tr>
                                <tr>
                                    <td className="border border-gray-300 p-2">
                                        <select required name="language_id" value={formData.last_annual_exam_marks.language_id} onChange={handleMarksChange} className="w-full border rounded p-1">
                                            <option value="">Select Additional Language</option>
                                            {languages.map(l => <option key={l.id} value={l.id}>{l.language}</option>)}
                                        </select>
                                    </td>
                                    <td className="border border-gray-300 p-2"><input required name="LanguageMarks" value={formData.last_annual_exam_marks.LanguageMarks} onChange={handleMarksChange} className="w-full border rounded p-1" /></td>
                                </tr>
                                <tr>
                                    <td className="border border-gray-300 p-2">Maths</td>
                                    <td className="border border-gray-300 p-2"><input required name="Maths" value={formData.last_annual_exam_marks.Maths} onChange={handleMarksChange} className="w-full border rounded p-1" /></td>
                                </tr>
                                <tr>
                                    <td className="border border-gray-300 p-2">Science</td>
                                    <td className="border border-gray-300 p-2"><input required name="Science" value={formData.last_annual_exam_marks.Science} onChange={handleMarksChange} className="w-full border rounded p-1" /></td>
                                </tr>
                                <tr>
                                    <td className="border border-gray-300 p-2">Social</td>
                                    <td className="border border-gray-300 p-2"><input required name="Social" value={formData.last_annual_exam_marks.Social} onChange={handleMarksChange} className="w-full border rounded p-1" /></td>
                                </tr>
                                <tr className="bg-gray-50">
                                    <td className="border border-gray-300 p-2 font-bold text-right">Total:</td>
                                    <td className="border border-gray-300 p-2"><input required name="Total" value={formData.last_annual_exam_marks.Total} onChange={handleMarksChange} className="w-full border rounded p-1" /></td>
                                </tr>
                                <tr className="bg-gray-50">
                                    <td className="border border-gray-300 p-2 font-bold text-right">Percentage / CGPA:</td>
                                    <td className="border border-gray-300 p-2"><input required name="PercentageCGPA" value={formData.last_annual_exam_marks.PercentageCGPA} onChange={handleMarksChange} className="w-full border rounded p-1" /></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="font-bold underline pb-2 block">Extra Curricular Participations</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                {extraCurricular.map((activity) => (
                                    <label key={activity.id} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            value={activity.id}
                                            checked={formData.extra_curricular_activity_id.includes(activity.id)}
                                            onChange={handleExtraCurricularChange}
                                        />
                                        <span>{activity.activity_name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div><label>Siblings Studying in SSV</label><select name="siblings_in_ssv" value={formData.siblings_in_ssv} onChange={handleChange} className="w-full p-2 border rounded mt-1"><option value="no">No</option><option value="yes">Yes</option></select></div>
                            {formData.siblings_in_ssv === 'yes' && (
                                <div><label>Childs Name & Class</label><input type="text" name="siblings_data" value={formData.siblings_data} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                            )}
                            <div><label>Been to SBV?</label><select name="been_to_sbv" value={formData.been_to_sbv} onChange={handleChange} className="w-full p-2 border rounded mt-1"><option value="no">No</option><option value="yes">Yes</option></select></div>
                            <div><label>Co-curricular Wish (Optional)</label><select name="co_curricular_activity_id" value={formData.co_curricular_activity_id} onChange={handleChange} className="w-full p-2 border rounded mt-1"><option value="">Select</option>{co_curricular_activities.map(c => <option key={c.id} value={c.id}>{c.activity_name}</option>)}</select></div>
                            <div><label>Candidate Aim</label><input type="text" name="candidate_aim" value={formData.candidate_aim} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                            <div><label>Achievements (if any)</label><input type="text" name="achievements" value={formData.achievements} onChange={handleChange} className="w-full p-2 border rounded mt-1" /></div>
                        </div>
                    </div>
                </div>

                {/* 5. UPLOADS */}
                <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                    <h3 className="text-xl font-bold text-orange-800 mb-4 border-b border-orange-300 pb-2">5. File Uploads</h3>

                    <div className="mb-6 p-4 bg-white border border-orange-100 rounded">
                        <label className="font-bold text-gray-700 mr-4">Does Student have any Health Issues?</label>
                        <select
                            value={hasHealthIssues}
                            onChange={(e) => setHasHealthIssues(e.target.value)}
                            className="p-2 border rounded shadow-sm focus:ring-orange-200"
                        >
                            <option value="no">No Health Problems</option>
                            <option value="yes">Yes (Proceed to upload Certificate)</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div><label>Student Photo</label><input type="file" accept="image/*" name="student_photo" onChange={handleFileChange} className="w-full p-2 border rounded mt-1 bg-white" /></div>
                        <div><label>Student Aadhaar</label><input type="file" accept=".pdf" name="student_adhaar" onChange={handleFileChange} className="w-full p-2 border rounded mt-1 bg-white" /></div>

                        {hasHealthIssues === 'yes' && (
                            <div className="bg-red-50 p-2 rounded border border-red-200"><label className="text-red-700 font-bold text-xs">Health Issues Certificate <span className="text-red-500">*</span></label><input required type="file" accept=".pdf" name="health_file" onChange={handleFileChange} className="w-full border rounded mt-1 bg-white" /></div>
                        )}

                        {!hasHealthIssues && <div />} {/* Grid spacer filler */}

                        <div><label>Mother Aadhaar</label><input type="file" accept=".pdf" name="mother_adhaar" onChange={handleFileChange} className="w-full p-2 border rounded mt-1 bg-white" /></div>
                        <div><label>Father Aadhaar</label><input type="file" accept=".pdf" name="father_adhaar" onChange={handleFileChange} className="w-full p-2 border rounded mt-1 bg-white" /></div>

                        <div><label>Student Signature</label><input type="file" accept=".pdf" name="student_signature" onChange={handleFileChange} className="w-full p-2 border rounded mt-1 bg-white" /></div>
                        <div><label>Mother Signature</label><input type="file" accept=".pdf" name="mother_signature" onChange={handleFileChange} className="w-full p-2 border rounded mt-1 bg-white" /></div>
                        <div><label>Father Signature</label><input type="file" accept=".pdf" name="father_signature" onChange={handleFileChange} className="w-full p-2 border rounded mt-1 bg-white" /></div>
                    </div>
                </div>

                <div className="flex justify-end gap-x-4 mt-8 bg-gray-50 py-4 px-6 rounded-lg shadow-inner border border-gray-200">
                    <button type="button" onClick={() => navigate('/admin/dashboard')} className="px-6 py-3 bg-gray-500 text-white rounded font-bold hover:bg-gray-600 transition">
                        Cancel
                    </button>
                    <button type="submit" disabled={loading} className="px-10 py-3 bg-blue-600 text-white font-bold rounded shadow hover:bg-blue-700 transition">
                        {loading ? 'Submitting Application...' : 'Save Manual Entry Details'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ManualAdmission;
